import { useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  initRequestPayments,
  setAlert,
  updateCarts,
  updateClient,
  updateOrder,
  updateRequestIsOrder,
} from "../../../reducers/userConfigSlice";
import { makeOrder, getOrderDetails, payOrder } from "../../../api/orders";
import { searchCustomerInformation } from "../../../api/commander";
import { CartOptions, OrderOptions } from "./payments-options";
import { OrderTotals } from "./totals";
import { OrderReview } from "./orderReview";
import { CheckoutDialog } from "./checkoutDialog";
import { store } from "../../../store";
import { flushAllProductChanges } from "../../../services/productSyncQueue";
import { useCartAmounts } from "../../../hooks/useCartAmounts";
import { isHomeDeliveryOrder } from "../../../utils/orderState";
import { getOrderTotalWithTax } from "../../../utils/pricing";
export const OrderFooter = ({
  setShowCartPayments,
  showCartPayments,
  clearCart,
  onOrderConfirmed,
  cartData,
}) => {
  const [review, setReview] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const dispatch = useDispatch(),
    creating = useRef(false),
    paying = useRef(false);
  const order = useSelector((s) => s.aplicationConfig.userConfig.order),
    cart = useSelector((s) => s.aplicationConfig.userConfig.cart),
    shipping = useSelector(
      (s) => s.aplicationConfig.corporationData.pointMethods.shipping,
    ),
    selectedId = useSelector(
      (s) => s.aplicationConfig.userConfig.selectedShippingMethodId,
    );
  const quote = useCartAmounts(),
    hasProducts = Boolean(cart?.ShoppingCartProducts?.length),
    isOrder = Boolean(order?.orderId);
  const method = useMemo(
    () =>
      (shipping || []).find(
        (m) => String(m.shippingMethodId) === String(selectedId),
      ),
    [shipping, selectedId],
  );
  const deliveryOrder = isOrder && isHomeDeliveryOrder(order, shipping || []);
  const total = isOrder ? getOrderTotalWithTax(order) : quote.amounts?.total;
  const notify = (message) =>
    dispatch(
      setAlert({
        called: true,
        type: "error",
        title: "No se pudo completar",
        message,
      }),
    );
  const ensureOrder = async () => {
    const current = store.getState().aplicationConfig.userConfig;
    if (current.order?.orderId) {
      const details = await getOrderDetails(current.order.orderId);
      if (details?.error !== false)
        throw Error(
          "No se pudo actualizar el pedido. Reintenta antes de cobrar.",
        );
      const next = details.data?.order || details.data;
      dispatch(updateOrder(next));
      return next;
    }
    if (creating.current) throw Error("El pedido ya se está creando.");
    creating.current = true;
    try {
      if (!(await flushAllProductChanges()))
        throw Error("Hay cambios sin guardar. Espera y vuelve a intentarlo.");
      const latest = store.getState().aplicationConfig.userConfig;
      if (!latest.cart?.shoppingCartId || !latest.client?.customerId || !method)
        throw Error("Revisa el cliente, la entrega y la comanda.");
      const address =
        latest.client?.addresses?.shippingAddresses?.[0] ||
        latest.client?.addresses?.addresses?.[0];
      if (method.homeDeliveryIsActive && !address)
        throw Error(
          "Selecciona una dirección de entrega del cliente antes de continuar.",
        );
      const response = await makeOrder({
        customerId: latest.client.customerId,
        shippingMethodId: method.shippingMethodId,
        shoppingCartId: latest.cart.shoppingCartId,
        address: method.homeDeliveryIsActive ? address : "",
      });
      if (response?.error !== false || !response.data?.orderId)
        throw Error(
          response?.message ||
            "No se confirmó la creación. Revisa el historial antes de repetir.",
        );
      // Preserve the confirmed ID immediately even if the following detail request fails.
      dispatch(updateOrder(response.data));
      dispatch(updateRequestIsOrder(true));
      const details = await getOrderDetails(response.data.orderId);
      if (details?.error !== false)
        throw Error(
          "El pedido se ha creado. No se pudo actualizar su detalle; reintenta la comprobación de ese pedido.",
        );
      const next = details.data?.order || details.data;
      dispatch(updateOrder(next));
      return next;
    } finally {
      creating.current = false;
    }
  };
  const confirm = async ({ expectedTotal }) => {
    if (paying.current) throw Error("El cobro ya se está confirmando.");
    paying.current = true;
    try {
      const current = store.getState().aplicationConfig;
      if (
        isHomeDeliveryOrder(
          current.userConfig.order,
          current.corporationData.pointMethods.shipping,
        )
      )
        throw Error("El reparto a domicilio lo cobra el repartidor.");
      if (!(await flushAllProductChanges()))
        throw Error(
          "Hay cambios sin guardar. Espera a sincronizar antes de cobrar.",
        );
      const latest = store.getState().aplicationConfig.userConfig,
        paidOrder = latest.order;
      const payments = (latest.request.activePayments || []).filter(
        (p) => Number(p.value) > 0,
      );
      const received = Number(
          payments.reduce((sum, p) => sum + Number(p.value), 0).toFixed(2),
        ),
        amount = getOrderTotalWithTax(paidOrder);
      if (
        !Number.isFinite(amount) ||
        !Number.isFinite(expectedTotal) ||
        Number(amount.toFixed(2)) !== Number(expectedTotal.toFixed(2))
      )
        throw Error(
          "El importe ha cambiado. Vuelve a revisar el método y el total antes de cobrar.",
        );
      if (
        !paidOrder?.orderId ||
        !Number.isFinite(amount) ||
        (!payments.length && amount !== 0) ||
        received < amount
      )
        throw Error("Revisa los importes antes de confirmar el cobro.");
      const response = await payOrder({
        orderId: paidOrder.orderId,
        customerId: latest.client.customerId,
        paymentMethodId: payments[0]?.paymentMethodId,
        paymentMethods: payments.length ? payments : undefined,
        amount: received,
        expectedTotal,
        expectedPointOfSaleId:
          store.getState().aplicationConfig.corporationData?.pointConfig
            ?.PointOfSaleConfigurationId,
        expectedOperatorId: latest.data?.userId,
      });
      let confirmed = response?.error === false ? response.data : null;
      let warning = "";
      if (
        confirmed?.orderId !== paidOrder.orderId ||
        confirmed?.paymentStatus !== "paid"
      ) {
        const check = await getOrderDetails(paidOrder.orderId);
        const found =
          check?.error === false ? check.data?.order || check.data : null;
        if (
          found?.orderId === paidOrder.orderId &&
          found.paymentStatus === "paid"
        ) {
          confirmed = found;
          warning =
            response?.message ||
            "Pago registrado. Revisa las notificaciones del pedido.";
        } else
          throw Error(
            response?.message ||
              "No se ha confirmado el cobro. Revisa el pedido antes de repetir.",
          );
      }
      const result = {
        order: {
          ...confirmed,
          displayNumber: confirmed.displayNumber || paidOrder.displayNumber,
        },
        total: amount,
        change: Number((received - amount).toFixed(2)),
        method:
          amount === 0
            ? "Sin cobro"
            : payments.length > 1
              ? "Pago dividido"
              : (
                  store.getState().aplicationConfig.corporationData.pointMethods
                    .payments || []
                ).find(
                  (m) => m.paymentMethodId === payments[0]?.paymentMethodId,
                )?.name || "Pago",
        warning,
      };
      dispatch(updateCarts([]));
      await clearCart();
      dispatch(initRequestPayments());
      dispatch(updateRequestIsOrder(false));
      const defaultResponse =
        await searchCustomerInformation("Cliente Contado");
      if (
        defaultResponse?.error === false &&
        defaultResponse.data?.customer?.[0]?.customerId
      )
        dispatch(updateClient(defaultResponse.data.customer[0]));
      onOrderConfirmed?.();
      return result;
    } catch (error) {
      notify(error.message);
      throw error;
    } finally {
      paying.current = false;
    }
  };
  const finishDelivery = async (confirmed) => {
    if (!confirmed?.orderId) throw Error("No se pudo confirmar el pedido.");
    let response;
    try {
      response = await searchCustomerInformation("Cliente Contado");
    } catch {
      /* The saved order remains confirmed if the default customer lookup is unavailable. */
    }
    if (
      store.getState().aplicationConfig.userConfig.order?.orderId !==
      confirmed.orderId
    )
      throw Error(
        "La venta ha cambiado. El pedido confirmado se conserva en el historial.",
      );
    if ((await clearCart()) === false)
      throw Error("El pedido está confirmado. Reintenta terminar la venta.");
    dispatch(initRequestPayments());
    if (response?.error === false && response.data?.customer?.[0]?.customerId)
      dispatch(updateClient(response.data.customer[0]));
    setShowCartPayments(false);
    onOrderConfirmed?.();
    dispatch(
      setAlert({
        called: true,
        type: "success",
        title: "Pedido confirmado",
        message: `${confirmed.displayNumber || "Pedido"} enviado a Cocina. El repartidor cobrará al entregar.`,
      }),
    );
  };
  const canPay =
    Number.isFinite(total) &&
    (isOrder
      ? order.paymentStatus !== "paid"
      : hasProducts && !quote.busy && !quote.error);
  return (
    <>
      <div className="tpvCheckoutFooter">
        <div className="order-footer-summary">
          <OrderTotals />
        </div>
        <div className="order-commander-action">
          <div className="order-footer-options">
            {isOrder ? (
              <OrderOptions
                handleShowCartPayments={() => setShowCartPayments(false)}
              />
            ) : (
              <CartOptions clearCart={clearCart} />
            )}
          </div>
          <div className="order-footer-content">
            <button
              className="order-commander-action-pay order-commander-action-pay-active"
              disabled={finishing || (!canPay && !deliveryOrder)}
              onClick={async () => {
                if (!isOrder) return setReview(true);
                if (deliveryOrder) {
                  if (creating.current) return;
                  creating.current = true;
                  setFinishing(true);
                  try {
                    await finishDelivery(order);
                  } catch (error) {
                    notify(error.message);
                  } finally {
                    creating.current = false;
                    setFinishing(false);
                  }
                  return;
                }
                setShowCartPayments(true);
              }}
            >
              {finishing
                ? "TERMINANDO…"
                : deliveryOrder
                  ? "TERMINAR VENTA"
                  : isOrder
                    ? "COBRAR"
                    : "CREAR PEDIDO"}
            </button>
          </div>
        </div>
      </div>
      {review && (
        <OrderReview
          lines={
            cartData?.ShoppingCartProducts || cart?.ShoppingCartProducts || []
          }
          total={total}
          onClose={() => setReview(false)}
          homeDelivery={Boolean(method?.homeDeliveryIsActive)}
          onConfirm={async () => {
            const confirmed = await ensureOrder();
            if (
              isHomeDeliveryOrder(confirmed, shipping || []) ||
              (!confirmed.fulfillment?.type && method?.homeDeliveryIsActive)
            )
              await finishDelivery(confirmed);
            setReview(false);
            setShowCartPayments(false);
          }}
        />
      )}
      {showCartPayments && !deliveryOrder && (
        <CheckoutDialog
          total={total}
          onEnsureOrder={async () => {
            if (!store.getState().aplicationConfig.userConfig.order?.orderId)
              throw Error("Confirma el pedido antes de cobrar.");
            const current = await ensureOrder();
            if (isHomeDeliveryOrder(current, shipping || []))
              throw Error("El reparto a domicilio lo cobra el repartidor.");
            return current;
          }}
          onConfirm={confirm}
          onClose={() => setShowCartPayments(false)}
        />
      )}
    </>
  );
};
